/** Matches backend `motorQuoteStatus` enum in liveController.ts */
import { liveStatusLabel } from "../helpers/reportingStatuses";

export const motorQuoteStatus = Object.freeze({
  quoteCreated: "quoteCreated",
  quoteReferredInPortal: "quoteReferredInPortal",
  quoteDeclinedInPortal: "quoteDeclinedInPortal",
  consentEmailSent: "consentEmailSent",
  sentToCustomer: "sentToCustomer",
  sentToBroker: "sentToBroker",
  quoteReferredBasedOnUnderwritingQuestions:
    "quoteReferredBasedOnUnderwritingQuestions",
  quoteDeclinedBasedOnUnderwritingQuestions:
    "quoteDeclinedBasedOnUnderwritingQuestions",
  policyBound: "policyBound",
});

/** Human-readable labels aligned with dashboard/reports via reportingStatuses. */
export const motorQuoteStatusLabel = Object.freeze(
  Object.values(motorQuoteStatus).reduce((acc, status) => {
    acc[status] = liveStatusLabel(status);
    return acc;
  }, {})
);

/**
 * @param {string|null|undefined} status - Raw status from API or UI
 * @returns {string} Label for display; unknown values pass through unchanged.
 */
export function getMotorQuoteStatusLabel(status) {
  if (status == null || status === "") {
    return "—";
  }
  if (status === "Not Complete") {
    return liveStatusLabel("Not Complete");
  }
  return liveStatusLabel(status);
}

/**
 * Raw status values for Quotes table filters (policyBound excluded; those rows are not listed).
 * Includes "Not Complete" for drafts without a status yet.
 */
export const motorQuoteFilterableStatuses = Object.freeze([
  motorQuoteStatus.quoteCreated,
  motorQuoteStatus.quoteReferredInPortal,
  motorQuoteStatus.quoteDeclinedInPortal,
  motorQuoteStatus.consentEmailSent,
  motorQuoteStatus.sentToCustomer,
  motorQuoteStatus.sentToBroker,
  motorQuoteStatus.quoteReferredBasedOnUnderwritingQuestions,
  motorQuoteStatus.quoteDeclinedBasedOnUnderwritingQuestions,
  "Not Complete",
]);

/** `{ text, value }[]` for Element UI `el-table-column` `:filters`. */
export function getMotorQuoteStatusFilterOptions() {
  return motorQuoteFilterableStatuses.map((value) => ({
    text: getMotorQuoteStatusLabel(value),
    value,
  }));
}

/** CSS modifier suffix for pill styling (see Quotes.vue). */
const PILL_MODIFIER = Object.freeze({
  [motorQuoteStatus.quoteCreated]: "quote-created",
  [motorQuoteStatus.quoteReferredInPortal]: "quote-referred-in-portal",
  [motorQuoteStatus.quoteDeclinedInPortal]: "quote-declined-in-portal",
  [motorQuoteStatus.consentEmailSent]: "consent-email-sent",
  [motorQuoteStatus.sentToCustomer]: "sent-to-customer",
  [motorQuoteStatus.sentToBroker]: "sent-to-broker",
  [motorQuoteStatus.quoteReferredBasedOnUnderwritingQuestions]:
    "quote-referred-underwriting",
  [motorQuoteStatus.quoteDeclinedBasedOnUnderwritingQuestions]:
    "quote-declined-underwriting",
  [motorQuoteStatus.policyBound]: "policy-bound",
  "Not Complete": "not-complete",
});

/**
 * @param {string|null|undefined} rawStatus - Value from API before label mapping
 * @returns {string} Classes for status pill
 */
export function getMotorQuoteStatusPillClass(rawStatus) {
  const base = "motor-quote-status-pill";
  if (rawStatus == null || rawStatus === "") {
    return `${base} ${base}--empty`;
  }
  const mod = PILL_MODIFIER[rawStatus];
  if (mod) {
    return `${base} ${base}--${mod}`;
  }
  return `${base} ${base}--other`;
}
